import React from 'react';
import { renderProcessedText } from '../../utils/textRenderer';

interface SelectedPlanCardProps {
  content: Array<{
    header: string;
    title: string;
    description: string;
    options: Array<{
      key: string;
      value: string;
    }>;
    arrivalText: string;
    arrivalSubText: string;
    paymentDetails: string;
    discountAmount: string;
    actualAmount: string;
    currency: string;
  }>;
  isCarousel?: boolean;
}

const SelectedPlanCard: React.FC<SelectedPlanCardProps> = ({ content, isCarousel }) => {
  // Render checkmark icon for options
  const renderCheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 12l2 2 4-4" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="12" r="10" stroke="#9CA3AF" strokeWidth="2" fill="none"/>
    </svg>
  );

  // Render truck/delivery icon
  const renderTruckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2.5 13.75H3.75C3.75 14.4404 4.30964 15 5 15C5.69036 15 6.25 14.4404 6.25 13.75H13.75C13.75 14.4404 14.3096 15 15 15C15.6904 15 16.25 14.4404 16.25 13.75H17.5V10L15 7.5H12.5V5H2.5V13.75Z" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="5" cy="13.75" r="1.25" stroke="#3B82F6" strokeWidth="1.5"/>
      <circle cx="15" cy="13.75" r="1.25" stroke="#3B82F6" strokeWidth="1.5"/>
    </svg>
  );

  // Render credit card icon
  const renderCardIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2.5" y="4.16663" width="15" height="11.6667" rx="2" stroke="#3B82F6" strokeWidth="1.5"/>
      <path d="M2.5 8.33337H17.5" stroke="#3B82F6" strokeWidth="1.5"/>
      <path d="M5.83337 12.5H7.50004" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );

  // Safety check for content
  if (!content || !Array.isArray(content) || content.length === 0) {
    return <div className="selected-plan-card-error">No plan details available</div>;
  }

  return (
    <div className="selected-plan-card" style={{ height: isCarousel ? '100%' : 'auto' }}>
      {content.map((plan, index) => (
        <div key={index} className="selected-plan-card__item">
          {/* Header */}
          {plan.header && (
            <div className="selected-plan-card__header">
              {renderProcessedText(plan.header)}
            </div>
          )}

          {/* Title */}
          {plan.title && (
            <h3 className="selected-plan-card__title">
              {renderProcessedText(plan.title)}
            </h3>
          )}

          {/* Description */}
          {plan.description && (
            <p className="selected-plan-card__description">
              {renderProcessedText(plan.description)}
            </p>
          )}

          {/* Plan Features/Options with checkmarks */}
          {plan.options && Array.isArray(plan.options) && plan.options.length > 0 && (
            <div className="selected-plan-card__features">
              {plan.options.map((option, optionIndex) => (
                <div key={optionIndex} className="selected-plan-card__feature">
                  {renderCheckIcon()}
                  <span className="selected-plan-card__feature-text">
                    {option.key}: {option.value}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Arrival Information */}
          {plan.arrivalText && (
            <div className="selected-plan-card__arrival">
              <div className="selected-plan-card__arrival-icon">
                {renderTruckIcon()}
              </div>
              <div className="selected-plan-card__arrival-content">
                <div className="selected-plan-card__arrival-text">
                  {renderProcessedText(plan.arrivalText)}
                </div>
                {plan.arrivalSubText && (
                  <div className="selected-plan-card__arrival-subtext">
                    {renderProcessedText(plan.arrivalSubText)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Details */}
          {plan.paymentDetails && (
            <div className="selected-plan-card__payment">
              <div className="selected-plan-card__payment-icon">
                {renderCardIcon()}
              </div>
              <div className="selected-plan-card__payment-text">
                {renderProcessedText(plan.paymentDetails)}
              </div>
            </div>
          )}

          {/* Pricing (if available) */}
          {(plan.discountAmount || plan.actualAmount) && (
            <div className="selected-plan-card__pricing">
              {plan.discountAmount && (
                <span className="selected-plan-card__discount-price">
                 {plan.discountAmount}/m
                </span>
              )}
              {plan.actualAmount && (
                <span className="selected-plan-card__actual-price">
                  Usually {plan.actualAmount}/m
                </span>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SelectedPlanCard;
